import { useEffect, useMemo, useState } from 'react'
import { Link, useParams } from '@tanstack/react-router'
import { fetchBlobUrl } from '../api/client'
import type { PreviewBlob } from '../api/client'
import { useCorrectDocument, useDocument, useSpamDecision } from '../api/hooks'
import { DOC_TYPES } from '../api/models'
import {
  Badge,
  ErrorState,
  Field,
  LoadingBlock,
  Section,
  formatAmount,
  formatBytes,
  formatDate,
} from '../components/ui'

/**
 * Mail HTML is sanitised on the server and rendered here inside a sandboxed iframe with
 * no allow-scripts and no allow-same-origin, so nothing in a mail can run or phone home.
 */
/** A white page with sane defaults, so the mail renders the way its sender meant it. */
const MAIL_FRAME_CSS = `
  <style>
    html, body { margin: 0; padding: 16px; background: #fff; color: #1a1815; }
    body { font: 15px/1.5 -apple-system, "Segoe UI", Helvetica, Arial, sans-serif; }
    img { max-width: 100%; height: auto; }
    table { max-width: 100%; }
    a { color: #285456; }
    blockquote { margin: 0 0 0 12px; padding-left: 12px; border-left: 3px solid #e7e3dc; color: #5c564e; }
  </style>
`

function MailViewer({ html }: { html: string }) {
  const [showImages, setShowImages] = useState(false)
  const [tall, setTall] = useState(false)
  const prepared = useMemo(
    () => MAIL_FRAME_CSS + (showImages ? html.replace(/data-blocked-src=/g, 'src=') : html),
    [html, showImages],
  )
  return (
    <div>
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <button type="button" className="btn-secondary" onClick={() => setShowImages((value) => !value)}>
          {showImages ? 'Externe Bilder wieder blockieren' : 'Externe Bilder laden'}
        </button>
        <button type="button" className="btn-ghost" onClick={() => setTall((value) => !value)}>
          {tall ? 'Kleiner' : 'Größer'}
        </button>
        <span className="hint">Skripte bleiben aus, externe Inhalte werden nicht geladen.</span>
      </div>
      <iframe
        title="Originalnachricht"
        sandbox=""
        referrerPolicy="no-referrer"
        srcDoc={prepared}
        className={`w-full rounded-md border border-paper-200 bg-white dark:border-paper-800 ${
          tall ? 'h-[80vh]' : 'h-[34rem]'
        }`}
      />
    </div>
  )
}

function BlobViewer({ sha, label }: { sha: string; label: string }) {
  const [preview, setPreview] = useState<PreviewBlob | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let created: string | null = null
    void fetchBlobUrl(sha)
      .then((value) => {
        created = value.url
        setPreview(value)
      })
      .catch((caught: Error) => setError(caught.message))
    return () => {
      if (created) URL.revokeObjectURL(created)
    }
  }, [sha])

  if (error) return <ErrorState error={new Error(error)} />
  if (!preview) return <LoadingBlock label={`${label} wird geladen`} />

  // Anything the server did not vouch for stays a download. A blob: URL inherits this
  // origin, so only types that cannot execute are ever put in a frame - the Blob is
  // re-typed in fetchBlobUrl, which is what actually closes that door.
  if (!preview.renderable) {
    return (
      <div className="card">
        <p className="text-sm">Für diesen Dateityp gibt es keine Vorschau.</p>
        <a className="btn-secondary mt-3" href={preview.url} download>
          Datei herunterladen
        </a>
      </div>
    )
  }

  if (preview.mediaType.startsWith('image/')) {
    return <img src={preview.url} alt={label} className="max-h-[38rem] rounded-md border border-paper-200" />
  }

  return (
    <div>
      {/* An <object> would be blocked outright - the CSP says object-src 'none'. */}
      <iframe
        title={label}
        src={preview.url}
        className="h-[38rem] w-full rounded-md border border-paper-200 bg-white dark:border-paper-800"
      />
      <p className="hint mt-2">
        <a className="underline" href={preview.url} download>
          Datei herunterladen
        </a>
      </p>
    </div>
  )
}

export function DocumentDetailPage() {
  const { documentId } = useParams({ from: '/dokumente/$documentId' })
  const query = useDocument(documentId)
  const correct = useCorrectDocument(documentId)
  const decideSpam = useSpamDecision()
  const [draft, setDraft] = useState<Record<string, unknown> | null>(null)

  if (query.isPending) return <LoadingBlock label="Dokument wird geladen" />
  if (query.isError) return <ErrorState error={query.error} onRetry={() => void query.refetch()} />

  const document = query.data
  const editing = draft !== null
  const value = (key: string) => (draft?.[key] ?? (document as Record<string, unknown>)[key] ?? '') as string

  return (
    <>
      <nav className="mb-4 text-sm">
        <Link to="/dokumente" className="text-paper-500 hover:underline">
          ← Dokumente
        </Link>
      </nav>

      <header className="mb-6">
        <h1 className="text-2xl">{document.title || 'Ohne Betreff'}</h1>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          {document.doc_type ? <Badge tone="accent">{document.doc_type}</Badge> : null}
          <Badge>{document.source}</Badge>
          <Badge tone={document.privacy_level === 'cloud_ok' ? 'warn' : 'ok'}>
            {document.privacy_level === 'cloud_ok' ? 'Cloud erlaubt' : 'nur lokal'}
          </Badge>
          {document.extraction_state === 'rules_only' ? <Badge>wartet auf Modell</Badge> : null}
          {document.user_corrected ? <Badge tone="ok">handkorrigiert</Badge> : null}
          {document.case_id ? (
            <Link to="/vorgaenge/$caseId" params={{ caseId: document.case_id }} className="chip hover:underline">
              Zum Vorgang
            </Link>
          ) : null}
        </div>
      </header>

      {document.spam_state !== 'clean' ? (
        <div
          role="alert"
          className="mb-6 rounded-md border border-red-300 bg-red-50 p-4 dark:border-red-900 dark:bg-red-950/40"
        >
          <p className="font-medium text-red-900 dark:text-red-100">
            {document.spam_state === 'quarantined'
              ? 'Wahrscheinlich Betrug — aussortiert'
              : 'Möglicherweise Betrug — bitte prüfen'}
          </p>
          <p className="mt-1 text-sm text-red-900 dark:text-red-100">
            Diese Nachricht erscheint nicht in der Suche. Klicke keine Links an und gib
            keine Daten ein, solange du nicht sicher bist.
          </p>
          {document.spam_reasons?.length ? (
            <ul className="mt-2 list-disc space-y-0.5 pl-5 text-sm text-red-900 dark:text-red-100">
              {document.spam_reasons.slice(0, 5).map((reason) => (
                <li key={reason}>{reason}</li>
              ))}
            </ul>
          ) : null}
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              type="button"
              className="btn-secondary"
              disabled={decideSpam.isPending}
              onClick={() => decideSpam.mutate({ id: documentId, decision: 'ok' })}
            >
              Ist in Ordnung — wieder freigeben
            </button>
            <button
              type="button"
              className="btn-ghost"
              disabled={decideSpam.isPending}
              onClick={() => decideSpam.mutate({ id: documentId, decision: 'spam' })}
            >
              Ist Betrug — aussortiert lassen
            </button>
          </div>
        </div>
      ) : null}

      <div className="grid gap-6 lg:grid-cols-[3fr_2fr]">
        <div>
          <Section title="Original">
            {document.body_html ? (
              <MailViewer html={document.body_html} />
            ) : document.letter_ocr_sha || document.letter_original_sha ? (
              <BlobViewer
                sha={(document.letter_ocr_sha ?? document.letter_original_sha) as string}
                label="Brief"
              />
            ) : (
              <pre className="card max-h-[28rem] overflow-auto whitespace-pre-wrap font-sans text-sm">
                {document.text || 'Kein Text vorhanden.'}
              </pre>
            )}
          </Section>

          {(document.attachments ?? []).length > 0 ? (
            <Section title="Anhänge">
              <ul className="space-y-2">
                {(document.attachments ?? []).map((attachment) => (
                  <li key={attachment.id} className="card flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate font-medium">{attachment.filename}</p>
                      <p className="hint">
                        {attachment.media_type} · {formatBytes(attachment.size)}
                      </p>
                    </div>
                    <a
                      className="btn-secondary"
                      href={`/api/blobs/${attachment.blob_sha}?download=true`}
                      download
                    >
                      Herunterladen
                    </a>
                  </li>
                ))}
              </ul>
            </Section>
          ) : null}
        </div>

        <div>
          <Section
            title="Extrahierte Felder"
            actions={
              editing ? null : (
                <button type="button" className="btn-secondary" onClick={() => setDraft({})}>
                  Korrigieren
                </button>
              )
            }
          >
            {editing ? (
              <form
                className="card"
                onSubmit={(event) => {
                  event.preventDefault()
                  correct.mutate(draft ?? {}, { onSuccess: () => setDraft(null) })
                }}
              >
                <Field label="Dokumenttyp">
                  {(props) => (
                    <select
                      {...props}
                      className="input"
                      value={value('doc_type')}
                      onChange={(event) => setDraft({ ...draft, doc_type: event.target.value })}
                    >
                      {DOC_TYPES.map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </select>
                  )}
                </Field>
                <Field label="Zusammenfassung">
                  {(props) => (
                    <textarea
                      {...props}
                      className="input"
                      rows={3}
                      value={value('summary')}
                      onChange={(event) => setDraft({ ...draft, summary: event.target.value })}
                    />
                  )}
                </Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Betrag">
                    {(props) => (
                      <input
                        {...props}
                        className="input"
                        inputMode="decimal"
                        value={value('amount_total')}
                        onChange={(event) => setDraft({ ...draft, amount_total: event.target.value })}
                      />
                    )}
                  </Field>
                  <Field label="Währung">
                    {(props) => (
                      <input
                        {...props}
                        className="input"
                        maxLength={3}
                        value={value('currency')}
                        onChange={(event) => setDraft({ ...draft, currency: event.target.value })}
                      />
                    )}
                  </Field>
                </div>
                <Field label="Fällig am">
                  {(props) => (
                    <input
                      {...props}
                      className="input"
                      type="date"
                      value={value('due_date')}
                      onChange={(event) => setDraft({ ...draft, due_date: event.target.value })}
                    />
                  )}
                </Field>
                <Field label="Absender">
                  {(props) => (
                    <input
                      {...props}
                      className="input"
                      value={(draft?.party_name as string) ?? document.party?.name ?? ''}
                      onChange={(event) => setDraft({ ...draft, party_name: event.target.value })}
                    />
                  )}
                </Field>
                <div className="flex gap-2">
                  <button type="submit" className="btn-primary" disabled={correct.isPending}>
                    Speichern
                  </button>
                  <button type="button" className="btn-ghost" onClick={() => setDraft(null)}>
                    Abbrechen
                  </button>
                </div>
                <p className="hint mt-2">
                  Korrekturen werden als Feedback gespeichert und von späteren KI-Läufen nicht
                  überschrieben.
                </p>
              </form>
            ) : (
              <dl className="card grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
                <dt className="text-paper-500">Absender</dt>
                <dd>{document.party?.name ?? '—'}</dd>
                <dt className="text-paper-500">Datum</dt>
                <dd>{formatDate(document.document_date)}</dd>
                <dt className="text-paper-500">Fällig</dt>
                <dd>{formatDate(document.due_date)}</dd>
                <dt className="text-paper-500">Betrag</dt>
                <dd>{formatAmount(document.amount_total, document.currency)}</dd>
                <dt className="text-paper-500">Wichtigkeit</dt>
                <dd>{document.importance} / 3</dd>
                <dt className="text-paper-500">Konfidenz</dt>
                <dd>
                  {document.confidence !== null && document.confidence !== undefined
                    ? `${Math.round(Number(document.confidence) * 100)} %`
                    : '—'}
                </dd>
                <dt className="text-paper-500">Zusammenfassung</dt>
                <dd>{document.summary || '—'}</dd>
              </dl>
            )}
          </Section>

          {(document.identifiers ?? []).length > 0 ? (
            <Section title="Nummern">
              <ul className="flex flex-wrap gap-2">
                {(document.identifiers ?? []).map((identifier) => (
                  <li key={identifier.id} className="chip">
                    <span className="text-paper-500">{identifier.kind}</span>
                    <span className="font-mono">{identifier.value}</span>
                  </li>
                ))}
              </ul>
            </Section>
          ) : null}

          {(document.line_items ?? []).length > 0 ? (
            <Section title="Positionen">
              <table className="w-full text-sm">
                <caption className="sr-only">Positionen dieses Dokuments</caption>
                <thead>
                  <tr className="text-left text-paper-500">
                    <th scope="col" className="py-1">
                      Bezeichnung
                    </th>
                    <th scope="col" className="py-1 text-right">
                      Menge
                    </th>
                    <th scope="col" className="py-1 text-right">
                      Betrag
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {(document.line_items ?? []).map((item) => (
                    <tr key={item.id} className="border-t border-paper-200 dark:border-paper-800">
                      <td className="py-1">{item.name}</td>
                      <td className="py-1 text-right">{item.quantity ?? '—'}</td>
                      <td className="py-1 text-right">{formatAmount(item.total, document.currency)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Section>
          ) : null}

          {(document.extraction_history ?? []).length > 0 ? (
            <Section title="Verlauf">
              <ul className="space-y-1 text-sm">
                {(document.extraction_history ?? []).map((entry, index) => {
                  const record = entry as Record<string, unknown>
                  return (
                    <li key={index} className="flex flex-wrap gap-2 text-paper-600 dark:text-paper-300">
                      <span className="font-mono text-xs">v{String(record.version)}</span>
                      <span>{String(record.source_kind)}</span>
                      {record.model ? <span className="text-paper-500">{String(record.model)}</span> : null}
                      {record.tier ? <Badge>{String(record.tier)}</Badge> : null}
                    </li>
                  )
                })}
              </ul>
            </Section>
          ) : null}
        </div>
      </div>
    </>
  )
}
